import {Col, Row} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import {useEntityTypes} from "../../utils/api";
import {Paper, Select} from "@mantine/core";
import {useCallback, useContext, useMemo, useState} from "react";
import MessageContext from "../../context/MessageContext.tsx";


interface ShieldMath {
    baseDamage: number;
    damage: number;
    critChance: number;
    regen: number;
    maxRounds: {
        shield: number | null;
        hull: number | null;
        disabled: number | null;
    };
}

const formatter = new Intl.NumberFormat("en-US");

const calculateDamagePerRound = (ship: ShipType): ShieldMath => {

    let dmg, critChance;
    if (ship.length > 1000) {
        dmg = ship.length / 4;
        critChance = 0.4;
    } else if (ship.length > 600) {
        dmg = ship.length / 6;
        critChance = 0.3;
    } else if (ship.length > 100) {
        dmg = ship.length / 10;
        critChance = 0.2;
    } else {
        dmg = 0;
        critChance = 0;
    }

    const regen = ship.shield * 0.05

    const crit = dmg + (dmg * critChance);
    const actualdmg = Math.max(0, (crit - regen));

    const maxRounds = {
        shield: actualdmg > 0 ? Math.ceil(ship.shield / actualdmg) : null,
        hull: actualdmg > 0 ? Math.ceil(ship.hull / actualdmg) : null,
        disabled: crit > 0 ? Math.ceil(ship.hull / crit) : null,
    };



    return {
        baseDamage: dmg,
        damage: crit,
        critChance: critChance,
        regen: regen,
        maxRounds: maxRounds,
    }
}

export default function AFShields() {

    const {ships} = useEntityTypes(['ships'], true);
    const {error} = useContext(MessageContext);

    const [selectedShip, setSelectedShip] = useState<ShipType | null>(null);

    const state: ShieldMath = useMemo(() => {
        if (selectedShip) {
            return calculateDamagePerRound(selectedShip)
        }
        return {
            damage: 0,
            baseDamage: 0,
            critChance: 0,
            regen: 0,
            maxRounds: {
                shield: null,
                hull: null,
                disabled: null
            }
        };
    }, [selectedShip]);


    const onSelect = useCallback((value: string | null) => {
        const ship = ships.find(s => s.id === value);
        if (ship) {
            setSelectedShip(ship);
        } else {
            error('')
        }
    }, [ships]);

    return (
        <div>
            <title>Asteroid Field Shield Calculator</title>
            <Row align="center">
                <Col><h1>AF Shield</h1></Col>
                <Col grow={1}/>
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Asteroid_Fields#Damage" target="_blank"><Icon
                    icon={IconMap.SwcRules}/> Full rules</a></Col>
            </Row>
            <Paper className="mb-3">
                <h2 className="mt-0 mb-4">Damage and Regen</h2>

                <Row mb={2}>
                    <Col>
                        <Select
                            value={selectedShip?.id}
                            data={ships.map(i => ({value: i.id, label: i.name}))}
                            onChange={onSelect}
                            searchable
                            placeholder="Select Ship"
                        />
                    </Col>
                </Row>

                {selectedShip ? (<>
                    <hr />
                    <Row mb={4}>
                        <Col grow={1}>
                            <div>Length</div>
                            <div>{formatter.format(selectedShip.length)}</div>
                        </Col>
                        <Col grow={1}>
                            <div>Base damage per round</div>
                            <div>{formatter.format(state.baseDamage)}</div>
                        </Col>
                        <Col grow={1}>
                            <div>AVG Damage with Crits</div>
                            <div>{formatter.format(state.damage)}</div>
                        </Col>
                        <Col grow={1}>
                            <div>Shield Regen</div>
                            <div>{formatter.format(state.regen)}</div>
                        </Col>
                    </Row>

                    <Row mb={2}>
                        <Col grow={1}>
                            <div>Avg Hours to start getting hull damage</div>
                            {state.maxRounds.shield ?? <i>Does not take damage</i>}
                        </Col>
                        <Col grow={1}>
                            <div>Avg Hours for ship to die (after shields are down)</div>
                            {state.maxRounds.hull ?? <i>Does not take damage</i>}
                        </Col>
                        <Col grow={1}>
                            <div>Avg Hours for ship to die (if disabled)</div>
                            {state.maxRounds.disabled ?? <i>Does not take damage</i>}
                        </Col>
                    </Row>
                </>) : null}

            </Paper>
        </div>
    )
}
